import React, { useEffect, useRef } from "react";
import useReducedMotion from "../hooks/useReducedMotion";

/**
 * The cursor is a lens, not an arrow.
 *
 * A `mix-blend-mode: difference` disc inverts whatever sits beneath it, so the
 * same element reads correctly on ink, on cream and on gold without a single
 * colour rule. It grows over anything interactive and prints a verb inside
 * itself when the target declares one via `data-lens`.
 */
export default function Lens() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const labelRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return undefined;

    const dot = dotRef.current;
    const ring = ringRef.current;
    const label = labelRef.current;
    if (!dot || !ring) return undefined;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let rx = mx;
    let ry = my;
    let raf = 0;

    document.documentElement.classList.add("has-lens");

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      dot.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%)`;

      const hit = e.target instanceof Element ? e.target.closest("[data-lens], a, button") : null;
      const verb = hit?.getAttribute?.("data-lens");
      ring.dataset.state = hit ? (verb ? "verb" : "hover") : "idle";
      if (label) label.textContent = verb || "";
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      // The ring trails the dot; the gap between them is the whole personality.
      rx += (mx - rx) * (reduced ? 1 : 0.16);
      ry += (my - ry) * (reduced ? 1 : 0.16);
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%, -50%)`;
    };

    const onDown = () => ring.classList.add("is-down");
    const onUp = () => ring.classList.remove("is-down");

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    raf = requestAnimationFrame(tick);

    return () => {
      document.documentElement.classList.remove("has-lens");
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
    };
  }, [reduced]);

  return (
    <div className="lens" aria-hidden="true">
      <span className="lens__dot" ref={dotRef} />
      <span className="lens__ring" ref={ringRef} data-state="idle">
        <span className="lens__label mono" ref={labelRef} />
      </span>
    </div>
  );
}
